import { useState, useMemo } from 'react';
import { Download, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import storage from '../utils/storage';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';

export default function ReportsPage() {
  const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);
  const user = auth.getCurrentUser();
  const [reportType, setReportType] = useState('inventory');
  const [dateRange, setDateRange] = useState('all');
  
  const products = storage.getProductsForUser(user);
  const transactions = storage.getTransactionsForUser(user);
  const categories = storage.getCategories();

  const inventoryReport = useMemo(() => {
    return {
      totalProducts: products.length,
      totalValue: products.reduce((sum, p) => sum + (p.costPrice * p.quantity), 0),
      lowStock: products.filter(p => p.quantity <= p.minStock && p.quantity > 0),
      outOfStock: products.filter(p => p.quantity === 0),
      byCategory: categories.map(cat => {
        const catProducts = products.filter(p => p.category === cat.name);
        return {
          category: cat.name,
          count: catProducts.length,
          value: catProducts.reduce((sum, p) => sum + (p.costPrice * p.quantity), 0)
        };
      })
    };
  }, [products, categories]);

  const salesReport = useMemo(() => {
    const filtered = transactions.filter(t => {
      const date = new Date(t.date);
      const now = new Date();
      if (dateRange === 'today') return date.toDateString() === now.toDateString();
      if (dateRange === 'week') return (now - date) / (1000 * 60 * 60 * 24) <= 7;
      if (dateRange === 'month') return (now - date) / (1000 * 60 * 60 * 24) <= 30;
      return true;
    });

    const revenue = filtered.reduce((sum, t) => sum + (t.total || 0), 0);
    const cogs = filtered.reduce((sum, t) => {
      if (typeof t.totalCost === 'number') return sum + t.totalCost;
      // fallback if historic transactions lack totalCost: estimate from items and current products
      const txCost = (t.products || []).reduce((s, item) => s + ((item.cost ?? (products.find(p => p.id === item.productId)?.costPrice || 0)) * item.quantity), 0);
      return sum + txCost;
    }, 0);
    const profit = revenue - cogs;
    const totalUnitsSold = filtered.reduce((sum, t) => sum + (t.products || []).reduce((s, item) => s + item.quantity, 0), 0);

    return {
      totalSales: filtered.length,
      totalUnitsSold,
      totalRevenue: revenue,
      totalCOGS: cogs,
      totalProfit: profit,
      profitMargin: revenue > 0 ? (profit / revenue) * 100 : 0,
      byPaymentMethod: ['cash', 'card', 'digital'].map(method => ({
        method,
        count: filtered.filter(t => t.paymentMethod === method).length,
        total: filtered.filter(t => t.paymentMethod === method).reduce((s, t) => s + (t.total || 0), 0)
      }))
    };
  }, [transactions, dateRange, products]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    const settings = storage.getSettings();
    
    doc.setFontSize(20);
    doc.text(settings.storeName || 'RetailFlow', 14, 20);
    doc.setFontSize(16);
    doc.text(`${reportType === 'inventory' ? 'Inventory' : 'Sales'} Report`, 14, 30);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 38);
    
    if (reportType === 'inventory') {
      const data = products.map(p => [
        p.name,
        p.sku,
        p.category,
        p.quantity,
        `${formatINR(p.costPrice)}`,
        `${formatINR(p.sellingPrice)}`,
        `${formatINR(p.costPrice * p.quantity)}`
      ]);
      
      doc.autoTable({
        startY: 45,
        head: [['Product', 'SKU', 'Category', 'Qty', 'Cost', 'Price', 'Value']],
        body: data
      });
    } else {
      const data = transactions.map(t => [
        new Date(t.date).toLocaleDateString(),
        t.id.slice(-8),
        t.customer,
        t.paymentMethod,
        `${formatINR(t.total)}`
      ]);
      
      doc.autoTable({
        startY: 45,
        head: [['Date', 'ID', 'Customer', 'Payment', 'Total']],
        body: data
      });
    }
    
    doc.save(`${reportType}-report-${Date.now()}.pdf`);
    toast.success('Report exported to PDF');
  };

  const exportToCSV = () => {
    let data, filename;
    
    if (reportType === 'inventory') {
      data = products.map(p => ({
        Name: p.name,
        SKU: p.sku,
        Category: p.category,
        Quantity: p.quantity,
        Unit: p.unit,
        CostPrice: p.costPrice,
        SellingPrice: p.sellingPrice,
        TotalValue: p.costPrice * p.quantity,
        Supplier: p.supplier,
        Location: p.location
      }));
      filename = 'inventory-report';
    } else {
      data = transactions.map(t => ({
        Date: new Date(t.date).toLocaleString(),
        TransactionID: t.id,
        Customer: t.customer,
        Subtotal: t.subtotal,
        Discount: t.discount,
        Total: t.total,
        PaymentMethod: t.paymentMethod,
        User: t.user
      }));
      filename = 'sales-report';
    }

    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}-${Date.now()}.csv`;
    link.click();
    toast.success('Report exported to CSV');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tight">Reports & Analytics</h1>
        <p className="text-muted-foreground mt-2">Generate and export business reports</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} />
            Report Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2 flex-1 min-w-[200px]">
              <label className="text-sm font-medium">Report Type</label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventory">Inventory Report</SelectItem>
                  <SelectItem value="sales">Sales Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reportType === 'sales' && (
              <div className="space-y-2 flex-1 min-w-[200px]">
                <label className="text-sm font-medium">Date Range</label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={exportToPDF} data-testid="export-pdf-btn">
                <Download size={16} className="mr-2" />
                Export PDF
              </Button>
              <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
                <Download size={16} className="mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {reportType === 'inventory' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Total Products</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black">{inventoryReport.totalProducts}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Total Value</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black text-primary">{formatINR(inventoryReport.totalValue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Low Stock</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black text-warning">{inventoryReport.lowStock.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Out of Stock</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black text-destructive">{inventoryReport.outOfStock.length}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Inventory by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {inventoryReport.byCategory.map(cat => (
                  <div key={cat.category} className="flex items-center justify-between p-4 border rounded-sm">
                    <div>
                      <p className="font-semibold">{cat.category}</p>
                      <p className="text-sm text-muted-foreground">{cat.count} products</p>
                    </div>
                    <p className="text-xl font-bold text-primary">{formatINR(cat.value)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {reportType === 'sales' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Total Sales</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black">{salesReport.totalSales}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Units Sold</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black">{salesReport.totalUnitsSold}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Selling Value</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black text-success">{formatINR(salesReport.totalRevenue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Buying Value (COGS)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black">{formatINR(salesReport.totalCOGS)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Profit</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black text-primary">{formatINR(salesReport.totalProfit)}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Sales by Payment Method</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {salesReport.byPaymentMethod.map(method => (
                  <div key={method.method} className="flex items-center justify-between p-4 border rounded-sm">
                    <div>
                      <p className="font-semibold capitalize">{method.method}</p>
                      <p className="text-sm text-muted-foreground">{method.count} transactions</p>
                    </div>
                    <p className="text-xl font-bold text-primary">{formatINR(method.total)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
